import { useState, useEffect } from 'react';
import { Phone, Mail, MessageSquare, UserCheck, Clock, RefreshCw, Inbox, ExternalLink, StickyNote, Send, ArrowLeft, CheckCircle2, Plus, Pencil, Copy, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Separator } from './ui/separator';
import { toast } from 'sonner';
import { leadAllocationAPI } from '../lib/advanced-features-api';
import { Input } from './ui/input';
import tokenStorage from '../services/token-storage';

interface Lead {
  id: string;
  propertyType: string;
  leadInfo: {
    name: string;
    email: string;
    phone: string;
    message: string;
  };
  timestamp: string;
  status: string;
  assignedTo: string;
  assignedAt?: string;
  source: string;
}

interface AgentLeadsProps {
  agentId: string;
}

const STATUS_LABELS: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive' }> = {
  new:       { label: 'New',       variant: 'default' },
  active:    { label: 'New',       variant: 'default' },
  contacted: { label: 'Contacted', variant: 'secondary' },
  qualified: { label: 'Qualified', variant: 'outline' },
  converted: { label: 'Converted', variant: 'outline' },
  closed:    { label: 'Closed',    variant: 'destructive' },
};

const STATUS_STEPS = ['new', 'contacted', 'qualified', 'converted'];

async function updateLead(leadId: string, fields: Record<string, any>) {
  return leadAllocationAPI.updateLead(leadId, fields);
}

function formatWhatsApp(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (!digits) return '';
  // South African local number: 0XXXXXXXXX → 27XXXXXXXXX
  if (digits.startsWith('0') && digits.length === 10) return '27' + digits.slice(1);
  return digits;
}

export function AgentLeads({ agentId }: AgentLeadsProps) {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Full-page detail view
  const [detailLead, setDetailLead] = useState<Lead | null>(null);
  const [detailNotes, setDetailNotes] = useState('');
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [commsMessage, setCommsMessage] = useState('');

  // Convert dialog
  const [convertTarget, setConvertTarget] = useState<Lead | null>(null);
  const [clientType, setClientType] = useState('buyer');
  const [convertNotes, setConvertNotes] = useState('');
  const [isConverting, setIsConverting] = useState(false);

  // Copy UUID feedback
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  // Edit lead dialog
  const [showEditLead, setShowEditLead] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [editForm, setEditForm] = useState({
    firstName: '', lastName: '', email: '', phone: '', propertyType: '', source: '',
  });

  // New Lead dialog (F-108)
  const [showNewLead, setShowNewLead] = useState(false);
  const [isCreatingLead, setIsCreatingLead] = useState(false);
  const [newLeadForm, setNewLeadForm] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    type: 'buyer', source: 'manual', notes: '',
  });

  const loadLeads = async () => {
    if (!agentId) return;
    let resolvedId = agentId.replace(/^cognito-/, '');
    try {
      const idToken = tokenStorage.getIdToken();
      if (idToken) {
        const payload = JSON.parse(atob(idToken.split('.')[1]));
        if (payload.sub) resolvedId = payload.sub;
      }
    } catch { /* fall back to agentId */ }
    setIsLoading(true);
    try {
      const data = await leadAllocationAPI.getAgentLeads(resolvedId);
      setLeads(data);
    } catch {
      toast.error('Failed to load leads');
      setLeads([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { loadLeads(); }, [agentId]);

  const openDetail = (lead: Lead) => {
    setDetailLead(lead);
    setDetailNotes(lead.leadInfo.message || '');
    setCommsMessage('');
  };

  const closeDetail = () => {
    setDetailLead(null);
    setDetailNotes('');
    setCommsMessage('');
  };

  const handleSaveNotes = async () => {
    if (!detailLead) return;
    setIsSavingNotes(true);
    try {
      await updateLead(detailLead.id, { notes: detailNotes });
      const updated = { ...detailLead, leadInfo: { ...detailLead.leadInfo, message: detailNotes } };
      setLeads(prev => prev.map(l => l.id === detailLead.id ? updated : l));
      setDetailLead(updated);
      toast.success('Notes saved');
    } catch (err: any) {
      toast.error(err.message || 'Failed to save notes');
    } finally {
      setIsSavingNotes(false);
    }
  };

  const handleMarkStatus = async (lead: Lead, newStatus: string) => {
    try {
      await updateLead(lead.id, { status: newStatus });
      const updated = { ...lead, status: newStatus };
      setLeads(prev => prev.map(l => l.id === lead.id ? updated : l));
      if (detailLead?.id === lead.id) setDetailLead(updated);
      toast.success(`Lead marked as ${STATUS_LABELS[newStatus]?.label ?? newStatus}`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to update status');
    }
  };

  const openEditLead = (lead: Lead) => {
    const [firstName = '', ...rest] = lead.leadInfo.name.split(' ');
    const lastName = rest.join(' ');
    setEditForm({
      firstName,
      lastName,
      email: lead.leadInfo.email || '',
      phone: lead.leadInfo.phone || '',
      propertyType: lead.propertyType || '',
      source: lead.source || '',
    });
    setShowEditLead(true);
  };

  const handleSaveEdit = async () => {
    if (!detailLead) return;
    if (!editForm.firstName || !editForm.lastName) {
      toast.error('First name and last name are required');
      return;
    }
    setIsSavingEdit(true);
    try {
      await updateLead(detailLead.id, {
        firstName: editForm.firstName.trim(),
        lastName: editForm.lastName.trim(),
        email: editForm.email.trim(),
        phone: editForm.phone.trim(),
        propertyType: editForm.propertyType,
        source: editForm.source,
      });
      const updatedName = `${editForm.firstName.trim()} ${editForm.lastName.trim()}`.trim();
      const updated: Lead = {
        ...detailLead,
        propertyType: editForm.propertyType || detailLead.propertyType,
        source: editForm.source || detailLead.source,
        leadInfo: {
          ...detailLead.leadInfo,
          name: updatedName,
          email: editForm.email.trim(),
          phone: editForm.phone.trim(),
        },
      };
      setLeads(prev => prev.map(l => l.id === detailLead.id ? updated : l));
      setDetailLead(updated);
      setShowEditLead(false);
      toast.success('Lead updated');
    } catch (err: any) {
      toast.error(err.message || 'Failed to update lead');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleConvert = async () => {
    if (!convertTarget) return;
    setIsConverting(true);
    try {
      await leadAllocationAPI.convertLead(convertTarget.id);
      const updated = { ...convertTarget, status: 'converted' };
      setLeads(prev => prev.map(l => l.id === convertTarget.id ? updated : l));
      if (detailLead?.id === convertTarget.id) setDetailLead(updated);
      toast.success(`${convertTarget.leadInfo.name} converted to client`);
      setConvertTarget(null);
    } catch (err: any) {
      toast.error(err.message || 'Failed to convert lead');
    } finally {
      setIsConverting(false);
    }
  };

  const handleCreateLead = async () => {
    if (!newLeadForm.firstName || !newLeadForm.lastName || !newLeadForm.email || !newLeadForm.phone) {
      toast.error('First name, last name, email and phone are required');
      return;
    }
    setIsCreatingLead(true);
    try {
      const lead = await leadAllocationAPI.createLead(newLeadForm);
      if (lead) setLeads(prev => [lead, ...prev]);
      toast.success('Lead created successfully');
      setShowNewLead(false);
      setNewLeadForm({ firstName: '', lastName: '', email: '', phone: '', type: 'buyer', source: 'manual', notes: '' });
    } catch (err: any) {
      toast.error(err.message || 'Failed to create lead');
    } finally {
      setIsCreatingLead(false);
    }
  };

  const activeLeads = leads.filter(l => l.status !== 'converted' && l.status !== 'closed');
  const convertedLeads = leads.filter(l => l.status === 'converted');

  const filtered = statusFilter === 'all'
    ? activeLeads
    : statusFilter === 'converted'
    ? convertedLeads
    : activeLeads.filter(l => l.status === statusFilter || (statusFilter === 'new' && l.status === 'active'));

  // ─── LOADING ────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground">
        <RefreshCw className="w-5 h-5 animate-spin mr-2" />
        Loading leads...
      </div>
    );
  }

  // ─── DETAIL PAGE ─────────────────────────────────────────────────────────────
  if (detailLead) {
    const statusCfg = STATUS_LABELS[detailLead.status] ?? { label: detailLead.status, variant: 'outline' as const };
    const isConverted = detailLead.status === 'converted';
    const waNumber = formatWhatsApp(detailLead.leadInfo.phone);
    const hasPhone = !!detailLead.leadInfo.phone;
    const hasEmail = !!detailLead.leadInfo.email;
    const currentStepIndex = STATUS_STEPS.indexOf(
      detailLead.status === 'active' ? 'new' : detailLead.status
    );

    return (
      <div className="space-y-6">
        {/* Back button */}
        <div className="flex items-center justify-between gap-3">
          <Button variant="ghost" size="sm" onClick={closeDetail} className="gap-1.5">
            <ArrowLeft className="w-4 h-4" />
            Back to Leads
          </Button>
          {!isConverted && (
            <Button variant="outline" size="sm" onClick={() => openEditLead(detailLead)} className="gap-1.5">
              <Pencil className="w-3.5 h-3.5" />
              Edit Lead
            </Button>
          )}
        </div>

        {/* Header card */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="space-y-2">
                <h2 className="text-2xl font-bold">{detailLead.leadInfo.name}</h2>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant={statusCfg.variant}>{statusCfg.label}</Badge>
                  <Badge variant="outline" className="capitalize">{detailLead.source.replace(/-/g, ' ')}</Badge>
                  <Badge variant="outline" className="capitalize">{detailLead.propertyType}</Badge>
                </div>
                <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" />
                  Received {new Date(detailLead.timestamp).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })}
                  {detailLead.assignedAt && (
                    <> · Assigned {new Date(detailLead.assignedAt).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })}</>
                  )}
                </p>
              </div>
              {isConverted && (
                <div className="flex items-center gap-2 text-green-500 font-medium">
                  <CheckCircle2 className="w-5 h-5" />
                  Converted to Client
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column: contact + comms */}
          <div className="lg:col-span-1 space-y-6">

            {/* Contact details */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Contact Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-4 h-4 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">Email</p>
                    {hasEmail ? (
                      <p className="text-sm font-medium truncate">{detailLead.leadInfo.email}</p>
                    ) : (
                      <p className="text-sm text-muted-foreground italic">Not provided</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-4 h-4 text-blue-500" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">Phone</p>
                    {hasPhone ? (
                      <p className="text-sm font-medium">{detailLead.leadInfo.phone}</p>
                    ) : (
                      <p className="text-sm text-muted-foreground italic">Not provided</p>
                    )}
                  </div>
                </div>
                <Separator />
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">Lead ID</p>
                    <p className="text-xs font-mono text-muted-foreground truncate">{detailLead.id}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 flex-shrink-0"
                    onClick={() => copyToClipboard(detailLead.id, detailLead.id)}
                    title="Copy Lead ID"
                  >
                    {copiedId === detailLead.id ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {hasEmail ? (
                  <a
                    href={`mailto:${detailLead.leadInfo.email}?subject=Following up on your enquiry`}
                    className="flex items-center gap-3 w-full p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                  >
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Send Email</p>
                      <p className="text-xs text-muted-foreground">Opens your mail app</p>
                    </div>
                  </a>
                ) : (
                  <div className="flex items-center gap-3 w-full p-3 rounded-lg border opacity-40 cursor-not-allowed">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Send Email</p>
                      <p className="text-xs text-muted-foreground">No email on file</p>
                    </div>
                  </div>
                )}

                {hasPhone && waNumber ? (
                  <a
                    href={`https://wa.me/${waNumber}`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-3 w-full p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4 text-green-500" />
                    <div>
                      <p className="text-sm font-medium">WhatsApp</p>
                      <p className="text-xs text-muted-foreground">Opens WhatsApp Web</p>
                    </div>
                  </a>
                ) : (
                  <div className="flex items-center gap-3 w-full p-3 rounded-lg border opacity-40 cursor-not-allowed">
                    <ExternalLink className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">WhatsApp</p>
                      <p className="text-xs text-muted-foreground">No phone on file</p>
                    </div>
                  </div>
                )}

                {hasPhone ? (
                  <a
                    href={`tel:${detailLead.leadInfo.phone}`}
                    className="flex items-center gap-3 w-full p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                  >
                    <Phone className="w-4 h-4 text-blue-500" />
                    <div>
                      <p className="text-sm font-medium">Call</p>
                      <p className="text-xs text-muted-foreground">Opens your phone app</p>
                    </div>
                  </a>
                ) : (
                  <div className="flex items-center gap-3 w-full p-3 rounded-lg border opacity-40 cursor-not-allowed">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Call</p>
                      <p className="text-xs text-muted-foreground">No phone on file</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

          </div>

          {/* Right column: enquiry, notes, status */}
          <div className="lg:col-span-2 space-y-6">

            {/* Original enquiry */}
            {detailLead.leadInfo.message && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                    <MessageSquare className="w-3.5 h-3.5" /> Original Enquiry
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{detailLead.leadInfo.message}</p>
                </CardContent>
              </Card>
            )}

            {/* Draft message */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                  <Send className="w-3.5 h-3.5" /> Draft Message
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Textarea
                  value={commsMessage}
                  onChange={e => setCommsMessage(e.target.value)}
                  placeholder={`Hi ${detailLead.leadInfo.name.split(' ')[0]}, thank you for your enquiry about a ${detailLead.propertyType}...`}
                  rows={4}
                  className="text-sm"
                />
                <div className="flex flex-wrap gap-2">
                  {hasEmail && (
                    <a
                      href={commsMessage.trim()
                        ? `mailto:${detailLead.leadInfo.email}?subject=Following up on your enquiry&body=${encodeURIComponent(commsMessage)}`
                        : `mailto:${detailLead.leadInfo.email}?subject=Following up on your enquiry`}
                      className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1.5 text-sm font-medium hover:bg-accent transition-colors"
                    >
                      <Mail className="w-3.5 h-3.5" /> Send via Email
                    </a>
                  )}
                  {hasPhone && waNumber && (
                    <a
                      href={commsMessage.trim()
                        ? `https://wa.me/${waNumber}?text=${encodeURIComponent(commsMessage)}`
                        : `https://wa.me/${waNumber}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1.5 text-sm font-medium hover:bg-accent transition-colors"
                    >
                      <ExternalLink className="w-3.5 h-3.5" /> Send via WhatsApp
                    </a>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Notes */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                  <StickyNote className="w-3.5 h-3.5" /> Agent Notes
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Textarea
                  value={detailNotes}
                  onChange={e => setDetailNotes(e.target.value)}
                  placeholder="Add your private notes about this lead (not visible to the client)..."
                  rows={6}
                  className="text-sm"
                />
                <Button onClick={handleSaveNotes} disabled={isSavingNotes}>
                  {isSavingNotes ? 'Saving...' : 'Save Notes'}
                </Button>
              </CardContent>
            </Card>

            {/* Status & progress */}
            {!isConverted && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Pipeline Status</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Progress bar */}
                  <div className="flex items-center gap-1">
                    {STATUS_STEPS.slice(0, 3).map((step, i) => {
                      const passed = currentStepIndex >= i;
                      return (
                        <div key={step} className="flex items-center flex-1">
                          <div className={`h-2 flex-1 rounded-full transition-colors ${passed ? 'bg-primary' : 'bg-muted'}`} />
                          {i < 2 && <div className="w-1" />}
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {(detailLead.status === 'new' || detailLead.status === 'active' || detailLead.status === 'allocated') && (
                      <Button variant="outline" onClick={() => handleMarkStatus(detailLead, 'contacted')}>
                        Mark as Contacted
                      </Button>
                    )}
                    {detailLead.status === 'contacted' && (
                      <Button variant="outline" onClick={() => handleMarkStatus(detailLead, 'qualified')}>
                        Mark as Qualified
                      </Button>
                    )}
                    {detailLead.status === 'qualified' && (
                      <Button
                        onClick={() => {
                          setConvertTarget(detailLead);
                          setConvertNotes(detailNotes || detailLead.leadInfo.message || '');
                          setClientType('buyer');
                        }}
                      >
                        <UserCheck className="w-4 h-4 mr-1.5" />
                        Convert to Client
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

          </div>
        </div>

        {/* Edit Lead dialog */}
        <Dialog open={showEditLead} onOpenChange={open => !open && setShowEditLead(false)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Lead Details</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>First Name <span className="text-destructive">*</span></Label>
                  <Input value={editForm.firstName} onChange={e => setEditForm(p => ({ ...p, firstName: e.target.value }))} placeholder="John" />
                </div>
                <div className="space-y-1">
                  <Label>Last Name <span className="text-destructive">*</span></Label>
                  <Input value={editForm.lastName} onChange={e => setEditForm(p => ({ ...p, lastName: e.target.value }))} placeholder="Smith" />
                </div>
              </div>
              <div className="space-y-1">
                <Label>Email</Label>
                <Input type="email" value={editForm.email} onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))} placeholder="john@example.com" />
              </div>
              <div className="space-y-1">
                <Label>Phone</Label>
                <Input value={editForm.phone} onChange={e => setEditForm(p => ({ ...p, phone: e.target.value }))} placeholder="+27821234567" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Property Type</Label>
                  <Select value={editForm.propertyType} onValueChange={v => setEditForm(p => ({ ...p, propertyType: v }))}>
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="residential">Residential</SelectItem>
                      <SelectItem value="apartment">Apartment</SelectItem>
                      <SelectItem value="house">House</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                      <SelectItem value="land">Land</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Source</Label>
                  <Select value={editForm.source} onValueChange={v => setEditForm(p => ({ ...p, source: v }))}>
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Manual</SelectItem>
                      <SelectItem value="referral">Referral</SelectItem>
                      <SelectItem value="cold-call">Cold Call</SelectItem>
                      <SelectItem value="open-day">Open Day</SelectItem>
                      <SelectItem value="social-media">Social Media</SelectItem>
                      <SelectItem value="website">Website</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" onClick={() => setShowEditLead(false)}>Cancel</Button>
                <Button onClick={handleSaveEdit} disabled={isSavingEdit}>
                  {isSavingEdit ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Convert dialog */}
        <Dialog open={!!convertTarget} onOpenChange={open => !open && setConvertTarget(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Convert to Client</DialogTitle>
            </DialogHeader>
            {convertTarget && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Converting <strong>{convertTarget.leadInfo.name}</strong> to a full client record.
                </p>
                <div className="space-y-2">
                  <Label>Client Type</Label>
                  <Select value={clientType} onValueChange={setClientType}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="buyer">Buyer</SelectItem>
                      <SelectItem value="seller">Seller</SelectItem>
                      <SelectItem value="renter">Renter</SelectItem>
                      <SelectItem value="investor">Investor</SelectItem>
                      <SelectItem value="enquiry">General Enquiry</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={convertNotes}
                    onChange={e => setConvertNotes(e.target.value)}
                    placeholder="Add any notes about this client..."
                    rows={3}
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setConvertTarget(null)}>Cancel</Button>
                  <Button onClick={handleConvert} disabled={isConverting}>
                    {isConverting ? 'Converting...' : 'Convert to Client'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ─── LEADS LIST ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* New Lead Dialog */}
      <Dialog open={showNewLead} onOpenChange={open => !open && setShowNewLead(false)}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Lead</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>First Name *</Label>
                <Input value={newLeadForm.firstName} onChange={e => setNewLeadForm(p => ({ ...p, firstName: e.target.value }))} placeholder="John" data-testid="new-lead-first-name" />
              </div>
              <div className="space-y-1">
                <Label>Last Name *</Label>
                <Input value={newLeadForm.lastName} onChange={e => setNewLeadForm(p => ({ ...p, lastName: e.target.value }))} placeholder="Smith" data-testid="new-lead-last-name" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Email *</Label>
              <Input type="email" value={newLeadForm.email} onChange={e => setNewLeadForm(p => ({ ...p, email: e.target.value }))} placeholder="john@example.com" data-testid="new-lead-email" />
            </div>
            <div className="space-y-1">
              <Label>Phone *</Label>
              <Input value={newLeadForm.phone} onChange={e => setNewLeadForm(p => ({ ...p, phone: e.target.value }))} placeholder="+27821234567" data-testid="new-lead-phone" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Type</Label>
                <Select value={newLeadForm.type} onValueChange={v => setNewLeadForm(p => ({ ...p, type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="buyer">Buyer</SelectItem>
                    <SelectItem value="seller">Seller</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Source</Label>
                <Select value={newLeadForm.source} onValueChange={v => setNewLeadForm(p => ({ ...p, source: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual</SelectItem>
                    <SelectItem value="referral">Referral</SelectItem>
                    <SelectItem value="cold-call">Cold Call</SelectItem>
                    <SelectItem value="open-day">Open Day</SelectItem>
                    <SelectItem value="social-media">Social Media</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label>Notes</Label>
              <Textarea value={newLeadForm.notes} onChange={e => setNewLeadForm(p => ({ ...p, notes: e.target.value }))} placeholder="Any initial notes..." rows={2} data-testid="new-lead-notes" />
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setShowNewLead(false)}>Cancel</Button>
              <Button onClick={handleCreateLead} disabled={isCreatingLead} data-testid="new-lead-submit">
                {isCreatingLead ? 'Creating...' : 'Create Lead'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">My Leads</h3>
          <p className="text-sm text-muted-foreground">
            {activeLeads.length} active · {convertedLeads.length} converted
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36 h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Active</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="qualified">Qualified</SelectItem>
              <SelectItem value="converted">Converted</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={loadLeads}>
            <RefreshCw className="w-3 h-3 mr-1" />
            Refresh
          </Button>
          <Button size="sm" onClick={() => setShowNewLead(true)} data-testid="new-lead-button">
            <Plus className="w-3 h-3 mr-1" />
            New Lead
          </Button>
        </div>
      </div>

      {/* Empty state */}
      {filtered.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <Inbox className="w-10 h-10 text-muted-foreground mb-3" />
            <p className="font-medium">
              {statusFilter === 'all' ? 'No leads assigned yet' : `No ${statusFilter} leads`}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              Leads will appear here once the admin allocates them to you.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Lead cards */}
      <div className="grid gap-3">
        {filtered.map(lead => {
          const statusCfg = STATUS_LABELS[lead.status] ?? { label: lead.status, variant: 'outline' as const };
          const isConverted = lead.status === 'converted';
          return (
            <Card
              key={lead.id}
              className={`transition-colors hover:border-primary/50 cursor-pointer ${isConverted ? 'opacity-60' : ''}`}
              onClick={() => openDetail(lead)}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold hover:text-primary transition-colors">
                        {lead.leadInfo.name}
                      </span>
                      <Badge variant={statusCfg.variant} className="text-xs">{statusCfg.label}</Badge>
                      <Badge variant="outline" className="text-xs capitalize">{lead.source.replace(/-/g, ' ')}</Badge>
                      <Badge variant="outline" className="text-xs capitalize">{lead.propertyType}</Badge>
                    </div>

                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      {lead.leadInfo.email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />{lead.leadInfo.email}
                        </span>
                      )}
                      {lead.leadInfo.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />{lead.leadInfo.phone}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(lead.timestamp).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    </div>

                    {lead.leadInfo.message && (
                      <p className="text-sm text-muted-foreground line-clamp-2 flex items-start gap-1">
                        <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        {lead.leadInfo.message}
                      </p>
                    )}
                  </div>

                  <div className="flex-shrink-0 flex items-center gap-2">
                    {isConverted
                      ? <CheckCircle2 className="w-5 h-5 text-green-500" />
                      : <ArrowLeft className="w-4 h-4 text-muted-foreground rotate-180" />
                    }
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
